import React from "react";
import {
  View,
  StyleSheet,
  ViewStyle,
  StyleProp,
  ScrollView,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { spacing } from "~/styles/tokens";
import { useTheme } from "~/styles/theme";

interface ScreenProps {
  children: React.ReactNode;
  scroll?: boolean;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  padded?: boolean;
  /** Content column width: narrow for forms, default for lists, wide for dense layouts. */
  width?: "narrow" | "default" | "wide";
  /** Vertically centre short content (sign-in style screens). */
  centered?: boolean;
}

const MAX_WIDTH = { narrow: 440, default: 680, wide: 960 } as const;

export const Screen: React.FC<ScreenProps> = ({
  children,
  scroll = false,
  style,
  contentStyle,
  padded = true,
  width = "default",
  centered = false,
}) => {
  const { colors } = useTheme();
  const inner = (
    <View
      style={[
        styles.content,
        centered ? styles.contentCentered : styles.contentFill,
        padded && styles.padded,
        { maxWidth: MAX_WIDTH[width] },
        contentStyle,
      ]}
    >
      {children}
    </View>
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }, style]}
      edges={["top", "left", "right"]}
    >
      {scroll ? (
        <ScrollView
          contentContainerStyle={styles.scroll}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {inner}
        </ScrollView>
      ) : (
        <View style={styles.fixed}>{inner}</View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    width: "100%",
  },
  contentFill: {
    flex: 1,
  },
  contentCentered: {
    marginVertical: "auto",
    paddingVertical: spacing.lg,
  },
  fixed: {
    flex: 1,
    alignItems: "center",
  },
  padded: {
    paddingHorizontal: spacing.md,
  },
  scroll: {
    flexGrow: 1,
    alignItems: "center",
    paddingBottom: spacing.lg,
  },
});
